import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft, Save, Download, Trash2, Plus } from "lucide-react";

export default function InvoiceEditorPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [invoice, setInvoice] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/invoices/${id}`);
                setInvoice(data);
            } catch (err) {
                toast.error(formatApiError(err));
                navigate("/invoices");
            }
        })();
    }, [id, navigate]);

    const update = (patch) => setInvoice({ ...invoice, ...patch });

    const total = useMemo(() => {
        return (invoice?.items || []).reduce((s, it) => s + (Number(it.quantity || 0) * Number(it.unit_price || 0)), 0);
    }, [invoice?.items]);

    if (!invoice) return <AppLayout><p className="text-sm text-ink-tertiary">Laden …</p></AppLayout>;

    const updateItem = (idx, patch) => {
        const next = [...(invoice.items || [])];
        next[idx] = { ...next[idx], ...patch };
        update({ items: next });
    };
    const addItem = () => update({ items: [...(invoice.items || []), { id: crypto.randomUUID(), description: "", quantity: 1, unit_price: 0 }] });
    const removeItem = (idx) => update({ items: invoice.items.filter((_, i) => i !== idx) });

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                invoice_number: invoice.invoice_number, invoice_date: invoice.invoice_date, due_date: invoice.due_date,
                client_name: invoice.client_name, client_company: invoice.client_company,
                client_street: invoice.client_street, client_zip: invoice.client_zip,
                client_city: invoice.client_city, client_country: invoice.client_country, client_email: invoice.client_email,
                title: invoice.title, intro_text: invoice.intro_text, footer_note: invoice.footer_note,
                items: invoice.items, currency: invoice.currency,
            };
            const { data } = await api.put(`/invoices/${id}`, payload);
            setInvoice(data);
            toast.success("Rechnung gespeichert");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const downloadPdf = async () => {
        await save();
        try {
            const res = await api.get(`/invoices/${id}/pdf`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `Rechnung_${invoice.invoice_number}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const fmtMoney = (v) => `${invoice.currency || "CHF"} ${Number(v || 0).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    return (
        <AppLayout>
            <div className="max-w-5xl">
                <Link to="/invoices" className="inline-flex items-center gap-1 text-sm text-ink-secondary hover:text-ink mb-4">
                    <ChevronLeft className="w-4 h-4" /> Zurück zu Rechnungen
                </Link>

                <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
                    <div>
                        <p className="text-xs uppercase tracking-[0.18em] text-ink-tertiary mb-2">Rechnung {invoice.invoice_number || "—"}</p>
                        <input
                            type="text"
                            value={invoice.title || ""}
                            onChange={(e) => update({ title: e.target.value })}
                            placeholder="Betreff der Rechnung"
                            data-testid="invoice-title"
                            className="w-full bg-transparent text-3xl font-semibold text-ink focus:outline-none border-b border-transparent hover:border-sand-300 focus:border-navy transition-colors py-1"
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <button type="button" onClick={save} disabled={saving} data-testid="invoice-save"
                            className="inline-flex items-center gap-2 bg-white border border-sand-300 text-ink rounded-md px-4 py-2 text-sm font-medium hover:border-navy disabled:opacity-50 transition-colors">
                            <Save className="w-4 h-4" /> {saving ? "Speichern …" : "Speichern"}
                        </button>
                        <button type="button" onClick={downloadPdf} data-testid="invoice-download"
                            className="inline-flex items-center gap-2 bg-terracotta text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-terracotta-dark transition-colors">
                            <Download className="w-4 h-4" /> PDF Download
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                    {/* Meta-Block */}
                    <div className="bg-white border border-sand-300 rounded-md p-5 space-y-4">
                        <h2 className="text-sm font-semibold uppercase tracking-[0.14em] text-ink-tertiary">Rechnung</h2>
                        <Field label="Rechnungsnummer" value={invoice.invoice_number} onChange={(v) => update({ invoice_number: v })} testid="invoice-number" />
                        <div className="grid grid-cols-2 gap-3">
                            <Field type="date" label="Rechnungsdatum" value={invoice.invoice_date} onChange={(v) => update({ invoice_date: v })} testid="invoice-date" />
                            <Field type="date" label="Fällig bis" value={invoice.due_date} onChange={(v) => update({ due_date: v })} testid="invoice-due-date" />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold uppercase tracking-[0.14em] text-ink-secondary mb-2">Währung</label>
                            <select value={invoice.currency || "CHF"} onChange={(e) => update({ currency: e.target.value })} data-testid="invoice-currency"
                                className="w-full bg-white border border-sand-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy">
                                <option value="CHF">CHF</option>
                                <option value="EUR">EUR</option>
                                <option value="USD">USD</option>
                            </select>
                        </div>
                    </div>

                    {/* Client-Block */}
                    <div className="bg-white border border-sand-300 rounded-md p-5 space-y-3">
                        <h2 className="text-sm font-semibold uppercase tracking-[0.14em] text-ink-tertiary">Kunde</h2>
                        <Field label="Firma (optional)" value={invoice.client_company} onChange={(v) => update({ client_company: v })} testid="invoice-client-company" />
                        <Field label="Name" value={invoice.client_name} onChange={(v) => update({ client_name: v })} testid="invoice-client-name" />
                        <Field label="Strasse & Nr." value={invoice.client_street} onChange={(v) => update({ client_street: v })} testid="invoice-client-street" />
                        <div className="grid grid-cols-3 gap-3">
                            <Field label="PLZ" value={invoice.client_zip} onChange={(v) => update({ client_zip: v })} testid="invoice-client-zip" />
                            <Field label="Ort" value={invoice.client_city} onChange={(v) => update({ client_city: v })} className="col-span-2" testid="invoice-client-city" />
                        </div>
                        <Field label="Land" value={invoice.client_country} onChange={(v) => update({ client_country: v })} testid="invoice-client-country" />
                        <Field label="E-Mail (optional)" type="email" value={invoice.client_email} onChange={(v) => update({ client_email: v })} testid="invoice-client-email" />
                    </div>
                </div>

                {/* Intro */}
                <div className="bg-white border border-sand-300 rounded-md p-5 mb-6">
                    <label className="block text-xs font-semibold uppercase tracking-[0.14em] text-ink-secondary mb-2">Einleitungstext</label>
                    <textarea
                        rows={2}
                        value={invoice.intro_text || ""}
                        onChange={(e) => update({ intro_text: e.target.value })}
                        placeholder="z.B. Vielen Dank für Ihr Vertrauen ..."
                        data-testid="invoice-intro"
                        className="w-full bg-white border border-sand-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy"
                    />
                </div>

                {/* Items */}
                <div className="bg-white border border-sand-300 rounded-md overflow-hidden mb-6">
                    <div className="px-5 py-4 border-b border-sand-300 flex items-center justify-between">
                        <h2 className="text-sm font-semibold uppercase tracking-[0.14em] text-ink-tertiary">Positionen</h2>
                        <button type="button" onClick={addItem} data-testid="invoice-add-item"
                            className="inline-flex items-center gap-1.5 text-xs text-navy hover:text-navy-hover border border-navy/30 hover:border-navy rounded px-3 py-1.5 transition-colors">
                            <Plus className="w-3.5 h-3.5" /> Position
                        </button>
                    </div>
                    <table className="w-full text-sm">
                        <thead className="bg-sand-100">
                            <tr>
                                <th className="px-4 py-2 text-left text-[10px] font-bold uppercase tracking-[0.16em] text-ink-secondary">Beschreibung</th>
                                <th className="px-4 py-2 text-right text-[10px] font-bold uppercase tracking-[0.16em] text-ink-secondary w-24">Menge</th>
                                <th className="px-4 py-2 text-right text-[10px] font-bold uppercase tracking-[0.16em] text-ink-secondary w-32">Einzel</th>
                                <th className="px-4 py-2 text-right text-[10px] font-bold uppercase tracking-[0.16em] text-ink-secondary w-32">Total</th>
                                <th className="w-10"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {(invoice.items || []).map((it, idx) => {
                                const line = Number(it.quantity || 0) * Number(it.unit_price || 0);
                                return (
                                    <tr key={it.id || idx} className="border-t border-sand-300" data-testid={`invoice-item-${idx}`}>
                                        <td className="px-4 py-2">
                                            <input type="text" value={it.description || ""} onChange={(e) => updateItem(idx, { description: e.target.value })}
                                                placeholder="Beschreibung"
                                                data-testid={`item-desc-${idx}`}
                                                className="w-full bg-white border border-sand-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-navy" />
                                        </td>
                                        <td className="px-4 py-2">
                                            <input type="number" step="0.01" value={it.quantity || 0} onChange={(e) => updateItem(idx, { quantity: parseFloat(e.target.value || "0") })}
                                                data-testid={`item-qty-${idx}`}
                                                className="w-full bg-white border border-sand-300 rounded-md px-3 py-1.5 text-sm text-right focus:outline-none focus:ring-2 focus:ring-navy" />
                                        </td>
                                        <td className="px-4 py-2">
                                            <input type="number" step="0.01" value={it.unit_price || 0} onChange={(e) => updateItem(idx, { unit_price: parseFloat(e.target.value || "0") })}
                                                data-testid={`item-price-${idx}`}
                                                className="w-full bg-white border border-sand-300 rounded-md px-3 py-1.5 text-sm text-right focus:outline-none focus:ring-2 focus:ring-navy" />
                                        </td>
                                        <td className="px-4 py-2 text-right font-semibold text-ink">{fmtMoney(line)}</td>
                                        <td className="px-2 py-2">
                                            <button onClick={() => removeItem(idx)} className="p-1 rounded hover:bg-terracotta/10 text-ink-secondary hover:text-terracotta transition-colors" data-testid={`item-remove-${idx}`}>
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    <div className="px-5 py-4 bg-sand-deep flex justify-end items-center gap-6">
                        <span className="text-sm font-semibold uppercase tracking-[0.14em] text-ink">Gesamtbetrag</span>
                        <span className="text-2xl font-bold text-terracotta" data-testid="invoice-total">{fmtMoney(total)}</span>
                    </div>
                </div>

                {/* Footer note */}
                <div className="bg-white border border-sand-300 rounded-md p-5 mb-6">
                    <label className="block text-xs font-semibold uppercase tracking-[0.14em] text-ink-secondary mb-2">Schlussbemerkung</label>
                    <input type="text" value={invoice.footer_note || ""} onChange={(e) => update({ footer_note: e.target.value })}
                        placeholder="z.B. Vielen Dank für Ihren Auftrag"
                        data-testid="invoice-footer"
                        className="w-full bg-white border border-sand-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy" />
                </div>
            </div>
        </AppLayout>
    );
}

function Field({ label, value, onChange, type = "text", placeholder = "", testid, className = "" }) {
    return (
        <div className={className}>
            <label className="block text-xs font-semibold uppercase tracking-[0.14em] text-ink-secondary mb-1.5">{label}</label>
            <input
                type={type}
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                data-testid={testid}
                className="w-full bg-white border border-sand-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy"
            />
        </div>
    );
}
